import React, { useState } from "react";
import { Button, Modal, Alert, Form, InputGroup, Badge } from "react-bootstrap";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { usePurchaseReceipt } from "../context/PurchaseReceiptContext";
import PurchaseReceiptForm from "../components/PurchaseReceiptForm";
import PurchaseReceiptTable from "../components/PurchaseReceiptTable";
import { useDeleteData } from "hooks/useApiHooks";
import Swal from "sweetalert2";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import PrintJson from "utils/printJson";
import { formatNumber, getContrastColor } from "utils/utils";
import { API_URL } from "utils/constants";
import WorkshopTopbar from "../components/WorkshopTopbar";

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

export default function PurchaseReceiptsPage() {
  const {
    purchaseReceipts,
    filterOptions,
    handleSearch,
    handleStatusFilter,
    handleDateFromFilter,
    handleDateToFilter,
    refetch,
    Pagination,
  } = usePurchaseReceipt();

  const [showModal, setShowModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);

  const { deleteData } = useDeleteData("/purchase-receipts", "purchase-receipts");

  const handleCreateReceipt = () => {
    setSelectedReceipt(null);
    setShowModal(true);
  };

  const handleEditReceipt = (receipt) => {
    setSelectedReceipt(receipt);
    setShowModal(true);
  };

  const handleViewReceipt = (receipt) => {
    setViewReceipt(receipt);
    setShowViewModal(true);
  };

  const handleDeleteReceipt = async (receipt) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete purchase receipt "${receipt.receipt_number}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteData(receipt.id);
        Swal.fire("Deleted!", "Purchase receipt has been deleted.", "success");
        refetch();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete purchase receipt.", "error");
      }
    }
  };

  const handleCancelReceipt = async (receipt) => {
    const result = await Swal.fire({
      title: "Cancel Purchase Receipt",
      text: `Are you sure you want to cancel purchase receipt "${receipt.receipt_number}"?`,
      icon: "warning",
      input: "textarea",
      inputPlaceholder: "Enter cancellation reason",
      inputAttributes: {
        "aria-label": "Cancellation reason",
      },
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, cancel it!",
      inputValidator: (value) => {
        if (!value) {
          return "Cancellation reason is required!";
        }
      },
    });

    if (result.isConfirmed) {
      try {
        const cancellationReason = result.value;
        const userData = localStorage.getItem("user");
        const user = userData ? JSON.parse(userData) : null;

        const payload = {
          cancelled_by: user?.id || "",
          cancellation_reason: cancellationReason,
          cancelled_at: new Date().toISOString(),
        };

        const response = await fetch(
          `${API_URL}/purchase-receipts/${receipt.id}/cancel`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // const data = await response.json();
        Swal.fire("Cancelled!", "Purchase receipt has been cancelled.", "success");
        refetch();
      } catch (error) {
        console.error("Error cancelling purchase receipt:", error);
        Swal.fire("Error!", "Failed to cancel purchase receipt.", "error");
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedReceipt(null);
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setViewReceipt(null);
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setSelectedReceipt(null);
    refetch();
    Swal.fire("Success!", "Purchase receipt has been saved.", "success");
  };

  const handleFormError = (error) => {
    Swal.fire(
      "Error!",
      error.message || "Failed to save purchase receipt.",
      "error"
    );
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID");
  };

  const getStatusBadgeVariant = (status, isCancelled) => {
    if (isCancelled) return "danger";
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "cancelled":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-3 workshop-page-scope">
        <WorkshopTopbar />
        <div
          className="workshop-content-panel"
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          {/* Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div className="mb-3 mb-md-0">
              <h2 className="h4 h-md-h2 mb-1 text-dark">
                Purchase Receipts Management
              </h2>
              <p className="text-muted small mb-0">
                Manage workshop purchase receipts and goods receiving
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <PermissionMiddleware allowedRoles={["superadmin", "mekanik"]}>
                <Button
                  variant="success"
                  onClick={handleCreateReceipt}
                  className="d-flex align-items-center gap-1"
                >
                  <IconPlus size={16} />
                  <span className="d-none d-sm-inline">
                    Process Receipt
                  </span>
                </Button>
              </PermissionMiddleware>
            </div>
          </div>

          {/* Filters */}
          <div className="row mb-4">
            <div className="col-md-6 col-lg-4 mb-3">
              <InputGroup>
                <InputGroup.Text>
                  <IconSearch size={16} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by receipt number, PO number, or supplier..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearch}
                />
              </InputGroup>
            </div>
            <div className="col-md-6 col-lg-2 mb-3">
              <Form.Select
                value={filterOptions.status}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-6 col-lg-3 mb-3">
              <Form.Control
                type="date"
                placeholder="Date From"
                value={filterOptions.date_from}
                onChange={(e) => handleDateFromFilter(e.target.value)}
              />
            </div>
            <div className="col-md-6 col-lg-3 mb-3">
              <Form.Control
                type="date"
                placeholder="Date To"
                value={filterOptions.date_to}
                onChange={(e) => handleDateToFilter(e.target.value)}
              />
            </div>
          </div>

          {/* Error Display */}
          {purchaseReceipts.error && (
            <Alert variant="danger" style={{ marginBottom: "24px" }}>
              <strong>Error loading purchase receipts:</strong>{" "}
              {purchaseReceipts.error}
            </Alert>
          )}

          {/* Purchase Receipts Table */}
          <PurchaseReceiptTable
            purchaseReceipts={purchaseReceipts}
            onEdit={handleEditReceipt}
            onDelete={handleDeleteReceipt}
            onView={handleViewReceipt}
            onCancel={handleCancelReceipt}
            Pagination={Pagination}
          />
        </div>
      </div>

      {/* CRUD Modal */}
      <PurchaseReceiptForm
        show={showModal}
        onHide={handleModalClose}
        initialData={selectedReceipt}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
      />

      {/* View Modal */}
      <Modal show={showViewModal} onHide={handleViewModalClose} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Purchase Receipt Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewReceipt && (
            <div>
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5>Receipt Information</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Receipt Number:</strong>
                        </td>
                        <td>{viewReceipt.receipt_number}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Received Date:</strong>
                        </td>
                        <td>{formatDate(viewReceipt.received_at)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Status:</strong>
                        </td>
                        <td>
                          <Badge
                            bg={getStatusBadgeVariant(viewReceipt.status, viewReceipt.is_cancelled)}
                            style={{
                              color: getContrastColor(
                                getStatusBadgeVariant(viewReceipt.status, viewReceipt.is_cancelled)
                              ),
                            }}
                          >
                            {viewReceipt.is_cancelled ? "Cancelled" : (viewReceipt.status || "Unknown")}
                          </Badge>
                        </td>
                      </tr>
                      {viewReceipt.is_cancelled && (
                        <>
                          <tr>
                            <td>
                              <strong>Cancelled By:</strong>
                            </td>
                            <td>{viewReceipt.cancelled_by?.name}</td>
                          </tr>
                          <tr>
                            <td>
                              <strong>Cancellation Reason:</strong>
                            </td>
                            <td>{viewReceipt.cancellation_reason}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h5>Purchase Order Details</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td>
                          <strong>PO Number:</strong>
                        </td>
                        <td>{viewReceipt.purchase_order?.po_number}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Warehouse:</strong>
                        </td>
                        <td>{viewReceipt.purchase_order?.warehouse?.name}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Supplier:</strong>
                        </td>
                        <td>{viewReceipt.purchase_order?.supplier?.name}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Requester:</strong>
                        </td>
                        <td>{viewReceipt.purchase_order?.requester?.name}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <h5>Received Items</h5>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Quantity Received</th>
                      <th className="text-end">Unit Price (Rp)</th>
                      <th className="text-end">Total Price (Rp)</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewReceipt.items?.map((item, index) => (
                      <tr key={index}>
                        <td>
                          {item.inventory?.name || `Item ${item.inventory_id}`}
                        </td>
                        <td>{item.quantity_received}</td>
                        <td className="text-end">
                          {formatNumber(item.actual_unit_price)}
                        </td>
                        <td className="text-end">
                          {formatNumber(item.quantity_received * item.actual_unit_price)}
                        </td>
                        <td>{item.note || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="row mt-4">
                <div className="col-md-6">
                  <h5>Notes</h5>
                  <p>{viewReceipt.note || "No notes"}</p>
                </div>
                <div className="col-md-6 text-end">
                  <div className="border p-3 rounded">
                    <p className="mb-0">
                      Total Items Received:{" "}
                      <strong>
                        {viewReceipt.items?.reduce((sum, item) => sum + item.quantity_received, 0) || 0}
                      </strong>
                    </p>
                  </div>
                </div>
              </div>
              <PrintJson
                data={viewReceipt}
                title="Purchase Receipt Detail JSON Data"
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleViewModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <PrintJson data={purchaseReceipts} title="Purchase Receipt JSON Data" />
    </LayoutDashboard>
  );
}
